import React, { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import SearchIcon from '@material-symbols/svg-300/rounded/search.svg'

import useLearningItemFilter from '../../state/queries/useLearningItemFilter'
import useLearningItemSuggestions from '../../state/queries/useLearningItemSuggestions'
import useRoutes from '../../state/useRoutes'
import Button from '../basics/Button/Button'
import Combobox from '../basics/Combobox/Combobox'

import styles from './Search.module.scss'

export default React.memo(function Search() {
  const { t } = useTranslation()
  const routes = useRoutes()

  const learningItemFilter = useLearningItemFilter({ dontSearch: true })

  const suggestions = useLearningItemSuggestions(learningItemFilter.search)

  useEffect(() => {
    suggestions.setQuery(learningItemFilter.search)
  }, [useLocation()])

  const handleSearchChanged = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => suggestions.setQuery(e.target.value as any),
    [suggestions.setQuery]
  )

  const handleSelect = useCallback(
    (value: string) => {
      if (value.startsWith('query:')) {
        routes.courses.withQuery(learningItemFilter, { search: value.slice(6) }).goto({})
        const f = document.activeElement as any
        f?.blur?.()
      } else {
        routes.course.goto({ uuid: value })
      }
    },
    [learningItemFilter, routes, suggestions.suggestions]
  )

  const handleSearchSubmitted = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      routes.courses.withQuery(learningItemFilter, e.currentTarget).goto({})
      const f = document.activeElement as any
      f && f.blur && f.blur()
    },
    [learningItemFilter.setSearch, routes]
  )

  const options = useMemo(() => {
    const options = []
    if (suggestions.query) {
      options.push({
        value: `query:${suggestions.query}`,
        label: t('browse courses.cta search in all courses', { term: suggestions.query }),
        selected: false,
      })
      for (const learningItem of suggestions.suggestions) {
        options.push({
          value: learningItem.uuid,
          label: `${t(`learning item.type.${learningItem.type}`)}: ${learningItem.title}`,
          selected: false,
        })
      }
    }
    return options
  }, [suggestions.query, suggestions.suggestions])

  return (
    <div className={styles.Search}>
      <form onSubmit={handleSearchSubmitted}>
        <Combobox
          name="search"
          placeholder={t('browse courses.search courses')}
          value={suggestions.query || ''}
          onChange={handleSearchChanged}
          onSelect={handleSelect}
          options={options}
          omitErrorDisplay
        />
        <Button tiny type="submit" className={styles.searchButton}>
          <SearchIcon />
        </Button>
      </form>
    </div>
  )
})
